import React, {useState, useEffect, useCallback, useRef} from "react";
import {
    SelectBox,
    SelectfieldDiv,
    SelectfieldLabel,
    SelectListBox,
    SelectSticky,
    SelectFilledLabel,
} from "./widgets/Styled";
import {useCombobox, UseComboboxState} from "downshift";
import {
    MULTI_SELECT,
    multiSelectStateReducerCallback,
    singleSelectStateReducerCallback,
    onSelectedItemCallback,
    SINGLE_SELECT,
    defaultItemToString,
} from "./utils";
import SelectMenu from "./widgets/SelectMenu";
import SelectMenuWithOptGroup from "./widgets/SelectMenuWithOptGroup";
import {ErrorHelperText} from "../Textfield/widgets/Styled";
import Header from "../../Registration/widgets/Header";
import SelectSearchHandler from "./widgets/SelectSearch";
import SelectStickyDoneButton from "./widgets/SelectStickyDoneButton";
import {BasicItem, SelectProps} from "./types";
import useDebounce from "../../../hooks/useDebounce";
import {validateField} from "../../../utils/validation";
import {useSelector} from "react-redux";
import {RootStore} from "../../../configureStore";
import {appMonitorLogger, isEqual} from "../../../utils/utils";
import {usePrevious} from "../../../hooks/utility";
import PopUp from "../../PopUp/Loadable";
import {useEventTracking} from "../../../hooks/useEventTracking";

const SelectV2 = function <T extends BasicItem>({
    id,
    placeholder,
    searchPlaceholder,
    type,
    items: passedItems,
    withOptGroup = false,
    optGroupItem: passedOptGroupItem,
    withSearch = false,
    placeHolderItemToString,
    itemToString,
    inputRFF,
    selectStyle = {},
    commonFieldStyle = {},
    isFocused,
    selectedOption = [],
    //isDisabled = false,
    isTouched = false,
    error,
    fullWidthSelectDropdown = false,
    isFilled = false,
    maxSelectedLength,
    onSelectedItemChange,
    isDisabled,
    menuLegend,
}: SelectProps<T>): JSX.Element {
    if (!validateField(passedItems)) {
        throw new Error("itemsData prop missing");
    }
    // get isMobile to render different view in mobile
    const isMobile = useSelector((state: RootStore) => state.layout.isMobile);
    // state which has selected item information contains array of options
    const previousSelectedItem = usePrevious(selectedOption);
    const [userSelectedItems, setUserSelectedItems] = useState<T[]>(selectedOption);
    const [isSelectPopupOpen, setIsSelectPopupOpen] = useState(false);
    const [userSearchInput, setUserSearchInput] = useState<string | undefined>(undefined);
    const previousSelectedOption = usePrevious(passedItems);
    const [items, setItems] = useState(passedItems);
    const [optGroupItem, setOptGroupItem] = useState(passedOptGroupItem);
    const debouncedSearchTerm = useDebounce(userSearchInput, 300);
    const selectRef = useRef<HTMLInputElement | null>(null);
    // console.log("selected option change", previousSelectedItem, userSelectedItems);
    const triggerEvent = useEventTracking();

    useEffect(() => {
        if (
            (typeof previousSelectedOption && !isEqual(previousSelectedOption, passedItems)) ||
            (typeof previousSelectedItem && !isEqual(previousSelectedItem, selectedOption))
        ) {
            // console.log("selected option change", previousSelectedOption, selectedOption);
            setUserSelectedItems(selectedOption);
            setUserSearchInput(undefined);
            setItems(passedItems);
            setOptGroupItem(passedOptGroupItem);
        }
    }, [
        previousSelectedOption,
        previousSelectedItem,
        setUserSelectedItems,
        setItems,
        selectedOption,
        setUserSearchInput,
        passedOptGroupItem,
        passedItems,
        setOptGroupItem,
    ]);

    useEffect(() => {
        if (withSearch && typeof debouncedSearchTerm !== "undefined" && isTouched) {
            if (debouncedSearchTerm) {
                void triggerEvent({
                    theme: "SA_CUSTOM_SELECT",
                    category: "search",
                    widget: placeholder,
                    label: debouncedSearchTerm,
                    eventName: "click",
                });
            }
            import("./filterWorker" /* webpackChunkName: 'selectworkerized' */)
                .then(async ({getItems, getFilterItemOptGroup}) => {
                    const itemPromise = withOptGroup
                        ? getFilterItemOptGroup(
                              debouncedSearchTerm,
                              passedOptGroupItem,
                              itemToString?.toString(),
                          )
                        : getItems(debouncedSearchTerm, passedItems, itemToString?.toString());
                    const response = await itemPromise;
                    if (withOptGroup && "flatFilterItems" in response) {
                        setOptGroupItem(response.filterItems);
                        response.flatFilterItems && setItems(response.flatFilterItems);
                    } else {
                        setItems(response.filterItems as T[]);
                    }
                })
                .catch((error: unknown) => {
                    appMonitorLogger({
                        message: `Exception: Select Web worker`,
                        error,
                    });
                });
        }
    }, [
        debouncedSearchTerm,
        withSearch,
        itemToString,
        passedItems,
        passedOptGroupItem,
        withOptGroup,
        isTouched,
        placeholder,
        triggerEvent,
    ]);

    const resetInputValue = useCallback(({isOpen}: UseComboboxState<T>) => {
        if (!isOpen) setUserSearchInput("");
    }, []);

    const isDocumentClicked = useSelector((state: RootStore) => state.layout.isUserInteracted);

    const {
        getComboboxProps,
        getToggleButtonProps,
        highlightedIndex,
        getInputProps,
        getItemProps,
        //getLabelProps,
        getMenuProps,
        isOpen,
        closeMenu,
        //   selectItem,
    } = useCombobox<T>(
        //@ts-expect-error: selectedItem equal to undefined leading to error so setting null
        {
            id: inputRFF ? inputRFF.name : id,
            items,
            itemToString,
            ...(typeof userSearchInput === "undefined"
                ? {inputValue: ""}
                : {inputValue: userSearchInput}),
            ...(withSearch
                ? {
                      onInputValueChange: ({inputValue}) => {
                          setUserSearchInput(inputValue);
                      },
                  }
                : {}),
            ...(type === MULTI_SELECT
                ? {
                      stateReducer: multiSelectStateReducerCallback(selectRef),
                      onSelectedItemChange: (obj) => {
                          const {selectedItem} = obj;
                          onSelectedItemCallback(
                              userSelectedItems,
                              setUserSelectedItems,
                              inputRFF,
                          )({selectedItem});
                          onSelectedItemChange && onSelectedItemChange(obj);
                      },
                      selectedItem: null,
                  }
                : {
                      stateReducer: singleSelectStateReducerCallback(selectRef),
                      onSelectedItemChange: (obj) => {
                          const {selectedItem} = obj;
                          selectedItem && setUserSelectedItems([selectedItem]);
                          inputRFF?.onChange(selectedItem);
                          onSelectedItemChange && onSelectedItemChange(obj);
                          setIsSelectPopupOpen(false);
                      },
                  }),
            ...(selectedOption && type === SINGLE_SELECT
                ? {initialSelectedItem: userSelectedItems[0]}
                : ""),
            circularNavigation: false,
        },
    );

    let MenuComponent = SelectMenu;
    if (withOptGroup) {
        MenuComponent = SelectMenuWithOptGroup;
    }
    const isError = error && !isOpen && isTouched;

    const jsx = (
        <SelectListBox
            {...getMenuProps()}
            isOpen={isOpen}
            fullWidthSelectDropdown={fullWidthSelectDropdown}
            withSearch={withSearch}
            type={type}
        >
            <SelectSticky>
                {isMobile && <Header placeholder={placeholder} />}

                {withSearch && (
                    <SelectSearchHandler
                        withSearch={withSearch}
                        isOpen={isOpen}
                        getInputProps={getInputProps}
                        resetInputValue={resetInputValue}
                        inputValue={userSearchInput}
                        placeholder={placeholder}
                        menuLegend={menuLegend}
                        searchPlaceholder={searchPlaceholder}
                        type={type}
                    />
                )}
            </SelectSticky>
            {(isOpen || isDocumentClicked) && (
                <MenuComponent
                    items={items}
                    getItemProps={getItemProps}
                    highlightedIndex={highlightedIndex}
                    isOpen={isOpen}
                    itemToString={itemToString}
                    type={type}
                    optGroupItem={optGroupItem}
                    userSelectedItems={userSelectedItems}
                    maxSelectedLength={maxSelectedLength}
                    withOptGroup={withOptGroup}
                    menuLegend={menuLegend}
                    withSearch={withSearch}
                    fieldName={inputRFF?.name ?? ""} // Used in inputRFF of checkbox component
                />
            )}

            {isOpen && type === MULTI_SELECT && (
                <SelectStickyDoneButton
                    items={items}
                    userSelectedItems={userSelectedItems}
                    closeMenu={closeMenu}
                />
            )}
        </SelectListBox>
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {value, ...exceptValue} = inputRFF ?? {};
    return (
        <>
            <SelectfieldDiv
                {...getComboboxProps()}
                selectStyle={selectStyle}
                commonFieldStyle={commonFieldStyle}
                isFocused={isOpen}
            >
                <SelectBox
                    {...getToggleButtonProps({
                        onKeyDown(event) {
                            const charCodeArr: Record<string, number> = {
                                Enter: 13,
                                ArrowDown: 40,
                            };
                            let keyCode: number;
                            if (event.key != undefined) {
                                keyCode = charCodeArr[event.key] || event.key.charCodeAt(0);
                            } else {
                                keyCode = event.which || event.charCode || event.keyCode || 0;
                            }

                            if (keyCode === 13 || keyCode === 40 || keyCode === 32) {
                                event.preventDefault();
                                event.currentTarget.click();
                            }
                        },
                        onClick() {
                            if (withSearch && !isMobile) {
                                setTimeout(() => {
                                    const {id: inputId} = getInputProps() as {
                                        id: string;
                                    };
                                    setTimeout(() => {
                                        document.getElementById(inputId)?.focus();
                                    }, 50);
                                });
                            }
                        },
                        disabled: isDisabled,
                    })}
                    {...(!withSearch ? getInputProps({ref: selectRef}) : {ref: selectRef})}
                    {...exceptValue}
                    isError={isError}
                    data-testid="selected-text-placeholder"
                    isFocused={isOpen}
                    tabIndex="0"
                    disabled={isDisabled}
                >
                    <SelectfieldLabel
                        isFilled={isFilled}
                        disabled={isDisabled}
                        isFocused={isOpen || isFocused}
                        isError={isError}
                        data-testid="select-placeholder"
                    >
                        {placeholder}
                    </SelectfieldLabel>
                    {isError && isFocused && (
                        <ErrorHelperText
                            ErrorDivStyle={{
                                mobile: {top: "48px"},
                                laptop: {top: "56px"},
                            }}
                        >
                            {error}
                        </ErrorHelperText>
                    )}
                    <SelectFilledLabel disabled={isDisabled}>
                        {userSelectedItems.length === 0
                            ? ""
                            : placeHolderItemToString &&
                              placeHolderItemToString(
                                  type === SINGLE_SELECT ? userSelectedItems[0] : userSelectedItems,
                              )}
                    </SelectFilledLabel>
                </SelectBox>
                {(!isSelectPopupOpen || !isOpen) && jsx}
                {isOpen && isMobile && (
                    <PopUp
                        isOpen={isOpen}
                        popUpName="selectPopup"
                        closeHandle={() => {
                            setIsSelectPopupOpen(false);
                            closeMenu();
                        }}
                        shouldCloseOnOverlayClick={false}
                        showHeading={false}
                        enableEscape={false}
                        showHeader={false}
                        onAfterOpen={() => {
                            setIsSelectPopupOpen(true);
                        }}
                    >
                        {isSelectPopupOpen && jsx}
                    </PopUp>
                )}
            </SelectfieldDiv>
        </>
    );
};

SelectV2.defaultProps = {
    placeHolderItemToString: defaultItemToString,
    itemToString: defaultItemToString,
    menuLegend: "",
};

export default SelectV2;
